import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import GetData from "../../services/GetData.js";
import useApiData from "../../services/useApiData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import GridRowContextMenu, {
    buildReturnsShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
    useSyncedUrlSearchQuery,
} from "../Helpers/GridRowContextMenu.jsx";
import SelectableCell, { openSelectableCell } from "../Helpers/SelectableCell.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import useServerGridSort from "../Helpers/useServerGridSort.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { RETURNS_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import { currencyFormatter } from "../Helpers/CurrencyFormat.js";
import { SingleSelectField, LookupField } from "../Products/TypedFields.jsx";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "../Products/catalogGridStyles.js";
import { mergeDataGridSx } from "../Helpers/dataGridStyles.js";
import "../Products/catalog.css";

const returnsGridSx = mergeDataGridSx(catalogDataGridSx, {
    "& .returns-over-thirty-row": {
        backgroundColor: "rgba(254, 226, 226, 0.65) !important",
        "&:hover": {
            backgroundColor: "rgba(254, 202, 202, 0.85) !important",
        },
        "&.Mui-selected": {
            backgroundColor: "rgba(252, 165, 165, 0.45) !important",
            "&:hover": {
                backgroundColor: "rgba(252, 165, 165, 0.6) !important",
            },
        },
    },
});

const STATUS_CHOICES = [
    "Pending Our Response",
    "Pending Customer Ship",
    "Pending ShipStation Response",
    "Pending UPS Response",
    "Pending Merchant Decision",
    "Received",
    "Non Refunded",
    "Refunded",
];

const CLAIM_REASONS = [
    "Item not As Described",
    "General Return",
    "Item not Shipped",
    "Item not Received by Buyer",
    "Dispute Return Condition",
    "Lost in Transit",
    "No Longer Wanted",
    "Lost After Delivery",
    "Incorrect item received",
    "Item Arrived Damaged",
    "Lower Price",
    "Defective/Broken",
    "Bought Somewhere Else",
    "Missing Items from Box",
    "Late to arrive",
    "Item and Shipping Box Damaged",
    "Difficult to set-up/Notcompatible",
];

const FILE_CLAIM = ["Not Needed", "Pending", "Filed", "Resolved"];
const RETURNED_STATUS = ["Pushed", "Not Pushed"];

const EMPTY_FORM = {
    rmaWalmart: "",
    disputeNumber: "",
    returnTrackingId: "",
    returnId: "",
    fileDate: "",
    quantity: "",
    status: "",
    claimReason: "",
    fileClaim: "",
    returnedItemStatus: "",
    checkAmount: "",
    orderNumber: "",
    orderAirtableId: "",
    customerName: "",
    skuName: "",
    itemName: "",
    platform: "",
};

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

/** OVER if return (file) date is 30+ calendar days after order date; UNDER otherwise. */
function overThirtyDaysFlag(orderDate, fileDate) {
    if (!orderDate || !fileDate) return "";
    const order = new Date(orderDate);
    const filed = new Date(fileDate);
    if (Number.isNaN(order.getTime()) || Number.isNaN(filed.getTime())) return "";
    const orderUtc = Date.UTC(order.getUTCFullYear(), order.getUTCMonth(), order.getUTCDate());
    const filedUtc = Date.UTC(filed.getUTCFullYear(), filed.getUTCMonth(), filed.getUTCDate());
    const days = Math.floor((filedUtc - orderUtc) / 86_400_000);
    return days >= 30 ? "OVER" : "UNDER";
}

function overThirtyChipSx(flag) {
    const base = { height: 24, fontWeight: 600, fontSize: 12, borderRadius: "8px" };
    if (flag === "OVER") {
        return { ...base, backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" };
    }
    if (flag === "UNDER") {
        return { ...base, backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac" };
    }
    return base;
}

function formatDateInput(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

function money(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return currencyFormatter(n, 2);
}

/** Marketplace APIs sometimes send name as an object; avoid showing "[object Object]". */
function customerDisplayName(value) {
    if (value == null || value === "") return "";
    if (typeof value === "string") {
        const text = value.trim();
        return text === "[object Object]" ? "" : text;
    }
    if (typeof value === "object") {
        const nested =
            value.fullName ||
            value.name ||
            [value.firstName, value.lastName].filter(Boolean).join(" ") ||
            value.username ||
            "";
        return customerDisplayName(nested);
    }
    return String(value);
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    const base = { height: 24, fontWeight: 600, fontSize: 12, borderRadius: "8px" };
    if (key === "ebay") {
        return { ...base, backgroundColor: "#fef3c7", color: "#854d0e", border: "1px solid #fcd34d" };
    }
    if (key === "walmart") {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }
    return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
}

function formFromReturn(item) {
    if (!item) return { ...EMPTY_FORM };
    return {
        rmaWalmart: item.rmaWalmart || "",
        disputeNumber: item.disputeNumber || "",
        returnTrackingId: item.returnTrackingId || "",
        returnId: item.returnId || "",
        fileDate: formatDateInput(item.fileDate),
        quantity: item.quantity == null ? "" : String(item.quantity),
        status: item.status || "",
        claimReason: item.claimReason || "",
        fileClaim: item.fileClaim || "",
        returnedItemStatus: item.returnedItemStatus || "",
        checkAmount: item.checkAmount == null ? "" : String(item.checkAmount),
        orderNumber: item.orderNumber || "",
        orderAirtableId: item.orderAirtableIds?.[0] || "",
        customerName: customerDisplayName(item.customerName),
        skuName: item.skuName || item.skuHelper || "",
        itemName: item.itemName || "",
        platform: item.platform || "",
    };
}

function linkedReturnCatalogs(item) {
    const rows = [];
    const seen = new Set();
    const push = (raw) => {
        if (!raw) return;
        const id =
            typeof raw === "object" ? String(raw._id || "") : String(raw || "");
        if (!id || seen.has(id)) return;
        seen.add(id);
        rows.push({
            id,
            itemId: typeof raw === "object" ? raw.itemId || "" : "",
        });
    };
    push(item?.receivedCatalogItem);
    for (const row of Array.isArray(item?.receivedCatalogItems)
        ? item.receivedCatalogItems
        : []) {
        push(row);
    }
    return rows;
}

function mapReturnRow(item) {
    const catalogs = linkedReturnCatalogs(item);
    const primary = catalogs[0] || null;
    const catalogItemIds = catalogs.map((row) => row.itemId).filter(Boolean);
    return {
        id: String(item._id),
        rma: item.rmaWalmart || "",
        returnId: item.returnId || "",
        orderNumber: item.orderNumber || "",
        customerName: customerDisplayName(item.customerName),
        platform: item.platform || "",
        status: item.status || "",
        claimReason: item.claimReason || "",
        fileClaim: item.fileClaim || "",
        fileDate: formatDate(item.fileDate),
        overThirtyDays: overThirtyDaysFlag(item.orderDate, item.fileDate),
        quantity: item.quantity ?? "",
        skuName: item.skuName || item.skuHelper || "",
        itemName: item.itemName || "",
        returnTrackingId: item.returnTrackingId || "",
        returnedItemStatus: item.returnedItemStatus || "",
        totalValue: item.totalValue,
        disputeNumber: item.disputeNumber || "",
        catalogMongoId: primary?.id || "",
        catalogItemId: primary?.itemId || "",
        catalogItemIds,
        catalogItemCount: catalogs.length,
        raw: item,
    };
}

export default function ReturnsGrid({ pulling = false, refreshKey = 0 }) {
    const navigate = useNavigate();
    const { searchInput, setSearchInput, searchQ } = useSyncedUrlSearchQuery();
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [rowCount, setRowCount] = useState(0);
    const [returns, setReturns] = useState([]);
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [orderOptions, setOrderOptions] = useState([]);
    const [orderInput, setOrderInput] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchingOrders, setSearchingOrders] = useState(false);
    const [receiving, setReceiving] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const {
        activeConditions,
        conjunction: gridConjunction,
        filterActive,
        barProps: filterBar,
    } = useGridFilter(RETURNS_FILTER_FIELDS);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQ]);

    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (row) => buildReturnsShareUrl(row),
                disabled: (row) =>
                    !String(
                        row?.rma || row?.orderNumber || row?.returnId || row?.skuName || ""
                    ).trim(),
                onCopied: () =>
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    }),
            }),
        ],
        []
    );

    const filtersParam = useMemo(() => {
        if (!activeConditions.length) return "";
        return JSON.stringify({
            conjunction: gridConjunction,
            conditions: activeConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [activeConditions, gridConjunction]);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.returns, {
        sortModel: [{ field: "fileDate", sort: "desc" }],
        columnVisibilityModel: {},
    });
    const { sortModel, sortForApi, onSortModelChange } = useServerGridSort(
        layout,
        "fileDate",
        "desc"
    );

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, sortForApi.sort, sortForApi.sortDir]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(paginationModel.page + 1),
            pageSize: String(paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        if (refreshKey) params.set("_r", String(refreshKey));
        return `api/broylesllc/returns?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filtersParam,
        refreshKey,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, isLoading, load, refetch } = useApiData(listUrl);

    useEffect(() => {
        const items = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
              ? data
              : [];
        setReturns(items);
        setRowCount(typeof data?.total === "number" ? data.total : items.length);
    }, [data]);

    const selectedIds = useMemo(() => [...(selectionModel?.ids ?? [])], [selectionModel]);

    const rows = useMemo(() => (returns || []).map(mapReturnRow), [returns]);
    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );

    const onSaved = refetch;

    const selectedRow = useMemo(
        () => (selectedIds.length === 1 ? rows.find((row) => row.id === String(selectedIds[0])) : null),
        [selectedIds, rows]
    );

    const startReturnProcess = async () => {
        const id = selectedRow?.id;
        if (!id || receiving) return;
        setReceiving(true);
        const result = await PostData({}, `broylesllc/returns/${id}/received`);
        setReceiving(false);
        if (!result?.success) {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.error?.response?.data?.message ||
                    result?.data?.message ||
                    "Could not start return process.",
            });
            return;
        }
        const payload = result.data || {};
        const searchQ =
            payload.searchQ ||
            payload.catalogItem?.itemId ||
            payload.catalogItems?.[0]?.itemId ||
            "";
        onSaved?.();
        if (!searchQ) {
            setSnack({
                open: true,
                color: "warning",
                message: payload.message || "Return marked Received, but no catalog item was linked.",
            });
            return;
        }
        const params = new URLSearchParams({ t: "1", q: searchQ });
        navigate(`/catalog-items?${params.toString()}`);
    };

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM });
        setSelectedOrder(null);
        setOrderInput("");
        setOrderOptions([]);
        setDialogOpen(true);
    };

    const openEdit = (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        const row = rows.find((item) => item.id === String(id));
        if (!row?.raw) return;
        const next = formFromReturn(row.raw);
        setEditingId(row.id);
        setForm(next);
        setSelectedOrder(
            next.orderNumber
                ? {
                      id: next.orderAirtableId || next.orderNumber,
                      orderNumber: next.orderNumber,
                      channel: next.platform,
                      itemTitle: next.itemName,
                      sku: next.skuName,
                      buyerName: next.customerName,
                  }
                : null
        );
        setOrderInput(next.orderNumber || "");
        setOrderOptions([]);
        setDialogOpen(true);
    };

    useEffect(() => {
        if (!dialogOpen) return undefined;
        const q = orderInput.trim();
        if (q.length < 2) {
            setOrderOptions([]);
            return undefined;
        }
        const timer = setTimeout(async () => {
            setSearchingOrders(true);
            const { getData } = await GetData(
                `broylesllc/orders/search?q=${encodeURIComponent(q)}`
            );
            setOrderOptions(Array.isArray(getData) ? getData : []);
            setSearchingOrders(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [orderInput, dialogOpen]);
    const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
    const setSelect = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        if (!form.rmaWalmart.trim() && !String(form.returnId || "").trim()) {
            setSnack({
                open: true,
                color: "error",
                message: "RMA or Return ID is required.",
            });
            return;
        }
        setSaving(true);
        const payload = {
            rmaWalmart: form.rmaWalmart.trim(),
            disputeNumber: form.disputeNumber,
            returnTrackingId: form.returnTrackingId,
            returnId: String(form.returnId || "").trim(),
            fileDate: form.fileDate ? new Date(form.fileDate).toISOString() : null,
            quantity: form.quantity === "" ? null : Number(form.quantity),
            status: form.status,
            claimReason: form.claimReason,
            fileClaim: form.fileClaim,
            returnedItemStatus: form.returnedItemStatus,
            checkAmount: form.checkAmount === "" ? null : Number(form.checkAmount),
            orderNumber: selectedOrder?.orderNumber || form.orderNumber || "",
            orderAirtableIds: selectedOrder?.id?.startsWith?.("rec")
                ? [selectedOrder.id]
                : form.orderAirtableId
                  ? [form.orderAirtableId]
                  : [],
            platform: selectedOrder?.channel || form.platform || "",
            itemName: selectedOrder?.itemTitle || form.itemName || "",
            skuName: selectedOrder?.sku || form.skuName || "",
            skuHelper: selectedOrder?.sku || form.skuName || "",
            customerName: selectedOrder?.buyerName || form.customerName || "",
            orderDate: selectedOrder?.orderDate || null,
            shipToZip: selectedOrder?.shipToZip || "",
            originalTrackingId: selectedOrder?.trackingNumber || "",
        };
        const result = editingId
            ? await PutData(payload, `broylesllc/returns/${editingId}`)
            : await PostData(payload, "broylesllc/returns");
        setSaving(false);
        if (result.success) {
            setDialogOpen(false);
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Return updated." : "Return created.",
            });
            onSaved?.();
        } else {
            setSnack({
                open: true,
                color: "error",
                message: result?.data?.message || "Save failed.",
            });
        }
    };

    const baseColumns = [
        { field: "rma", headerName: "RMA", minWidth: 160, flex: 0.8 },
        { field: "returnId", headerName: "Return ID", minWidth: 140 },
        {
            field: "orderNumber",
            headerName: "Order",
            minWidth: 150,
            renderCell: (params) => {
                const label = params.value || "";
                if (!label) {
                    return (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            —
                        </Typography>
                    );
                }
                const query = new URLSearchParams({
                    order: label,
                    fromReturn: params.row.id,
                });
                return (
                    <Link
                        component={RouterLink}
                        to={`/orders?${query.toString()}`}
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                    >
                        {label}
                    </Link>
                );
            },
        },
        { field: "customerName", headerName: "Customer", minWidth: 160 },
        {
            field: "platform",
            headerName: "Platform",
            minWidth: 120,
            renderCell: (params) => {
                const text = params.value || "";
                if (!text) {
                    return (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            —
                        </Typography>
                    );
                }
                return <Chip size="small" label={text} sx={channelChipSx(text)} />;
            },
        },
        { field: "status", headerName: "Status", minWidth: 200, type: "singleSelect", editable: true, valueOptions: STATUS_CHOICES, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "claimReason", headerName: "Claim reason", minWidth: 220, flex: 1, type: "singleSelect", editable: true, valueOptions: CLAIM_REASONS, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "fileClaim", headerName: "File claim", minWidth: 140, type: "singleSelect", editable: true, valueOptions: FILE_CLAIM, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "returnedItemStatus", headerName: "Returned item", minWidth: 150, type: "singleSelect", editable: true, valueOptions: RETURNED_STATUS, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "fileDate", headerName: "File date", minWidth: 120 },
        {
            field: "overThirtyDays",
            headerName: "Over 30 days",
            minWidth: 130,
            renderCell: (params) => {
                const flag = params.value || "";
                if (!flag) {
                    return (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            —
                        </Typography>
                    );
                }
                return <Chip size="small" label={flag} sx={overThirtyChipSx(flag)} />;
            },
        },
        { field: "quantity", headerName: "Qty", width: 70, align: "right", headerAlign: "right" },
        { field: "skuName", headerName: "SKU", minWidth: 160 },
        { field: "itemName", headerName: "Item", minWidth: 200, flex: 1.2 },
        {
            field: "catalogItemId",
            headerName: "Catalog item",
            minWidth: 180,
            renderCell: (params) => {
                const ids = Array.isArray(params.row.catalogItemIds)
                    ? params.row.catalogItemIds.filter(Boolean)
                    : [];
                const label = ids[0] || params.value || "";
                const count = Math.max(params.row.catalogItemCount || 0, ids.length);
                if (!label && !params.row.catalogMongoId) {
                    return (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            —
                        </Typography>
                    );
                }
                const query = new URLSearchParams({ t: "6" });
                // Shared ret_ prefix finds all units when qty > 1.
                const search =
                    count > 1 && label.includes("_")
                        ? label.replace(/-\d+$/, "")
                        : label;
                if (search) query.set("q", search);
                return (
                    <Link
                        component={RouterLink}
                        to={`/catalog-items?${query.toString()}`}
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                    >
                        {count > 1 ? `${label} · ${count} items` : label || "View"}
                    </Link>
                );
            },
        },
        { field: "returnTrackingId", headerName: "Return tracking", minWidth: 160 },
        {
            field: "totalValue",
            headerName: "Total value",
            width: 120,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) => money(value) || "—",
        },
    ];

    const columns = layout.applyWidths(baseColumns);

    return (
        <>
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search RMA, order, customer, SKU…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <span className="catalog-selection-hint">
                        {rowCount.toLocaleString()} return{rowCount === 1 ? "" : "s"}
                        {filterActive || searchQ ? " matching" : ""}
                    </span>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1 || receiving}
                        startIcon={
                            receiving ? (
                                <CircularProgress size={14} />
                            ) : (
                                <Inventory2OutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={startReturnProcess}
                        sx={catalogQuietButtonSx}
                    >
                        Start return process
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add return
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading || pulling)}
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onCellClick={(params) =>
                            openSelectableCell(params, [
                                "status",
                                "claimReason",
                                "fileClaim",
                                "returnedItemStatus",
                            ])
                        }
                        onCellDoubleClick={(params) => {
                            if (["status", "claimReason", "fileClaim", "returnedItemStatus"].includes(params.field)) return;
                            openEdit(params.id);
                        }}
                        processRowUpdate={async (newRow, oldRow) => {
                            const payload = {};
                            for (const key of ["status", "claimReason", "fileClaim", "returnedItemStatus"]) {
                                if ((newRow[key] || "") !== (oldRow[key] || "")) payload[key] = newRow[key] || "";
                            }
                            if (!Object.keys(payload).length) return oldRow;
                            const result = await PutData(payload, `broylesllc/returns/${newRow.id}`);
                            if (!result.success) {
                                const message = result?.data?.message || "Save failed.";
                                setSnack({ open: true, color: "error", message });
                                throw new Error(message);
                            }
                            setSnack({ open: true, color: "success", message: "Saved." });
                            onSaved?.();
                            return { ...newRow, raw: { ...newRow.raw, ...payload } };
                        }}
                        onProcessRowUpdateError={() => {}}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        getRowClassName={(params) =>
                            params.row?.overThirtyDays === "OVER" ? "returns-over-thirty-row" : ""
                        }
                        slotProps={gridSlotProps}
                        sx={returnsGridSx}
                        localeText={{ noRowsLabel: "No returns yet." }}
                    />
                </div>
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingId ? "Edit return" : "Add return"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        <TextField
                            label="RMA (Walmart)"
                            size="small"
                            value={form.rmaWalmart}
                            onChange={setField("rmaWalmart")}
                            fullWidth
                            helperText="Provide RMA and/or Return ID"
                        />
                        <Autocomplete
                            size="small"
                            options={orderOptions}
                            loading={searchingOrders}
                            value={selectedOrder}
                            inputValue={orderInput}
                            onInputChange={(_event, value, reason) => {
                                if (reason === "reset") return;
                                setOrderInput(value);
                            }}
                            onChange={(_event, order) => {
                                setSelectedOrder(order);
                                setOrderInput(order?.orderNumber || "");
                                if (!order) return;
                                setForm((prev) => ({
                                    ...prev,
                                    orderNumber: order.orderNumber || "",
                                    orderAirtableId: order.id || "",
                                    platform: order.channel || "",
                                    itemName: order.itemTitle || "",
                                    skuName: order.sku || "",
                                    customerName: order.buyerName || "",
                                    quantity:
                                        prev.quantity ||
                                        (order.quantity == null ? "" : String(order.quantity)),
                                }));
                            }}
                            getOptionLabel={(option) => option?.orderNumber || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            filterOptions={(options) => options}
                            noOptionsText={
                                orderInput.trim().length < 2
                                    ? "Type at least 2 characters"
                                    : "No matching orders"
                            }
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <span>
                                        <strong>{option.orderNumber}</strong>
                                        {option.channel ? ` · ${option.channel}` : ""}
                                        {option.itemTitle ? ` · ${option.itemTitle}` : ""}
                                    </span>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Match order"
                                    placeholder="Search order number"
                                />
                            )}
                        />
                        <TextField
                            label="Dispute number"
                            size="small"
                            value={form.disputeNumber}
                            onChange={setField("disputeNumber")}
                            fullWidth
                        />
                        <TextField
                            label="Return tracking ID"
                            size="small"
                            value={form.returnTrackingId}
                            onChange={setField("returnTrackingId")}
                            fullWidth
                        />
                        <TextField
                            label="Return ID"
                            size="small"
                            value={form.returnId}
                            onChange={setField("returnId")}
                            fullWidth
                        />
                        <TextField
                            label="File date"
                            size="small"
                            type="date"
                            value={form.fileDate}
                            onChange={setField("fileDate")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Quantity"
                            size="small"
                            type="number"
                            value={form.quantity}
                            onChange={setField("quantity")}
                            fullWidth
                        />
                        <SingleSelectField
                            label="Status"
                            value={form.status}
                            onChange={setSelect("status")}
                            choices={STATUS_CHOICES}
                        />
                        <SingleSelectField
                            label="Claim reason"
                            value={form.claimReason}
                            onChange={setSelect("claimReason")}
                            choices={CLAIM_REASONS}
                        />
                        <SingleSelectField
                            label="File claim"
                            value={form.fileClaim}
                            onChange={setSelect("fileClaim")}
                            choices={FILE_CLAIM}
                        />
                        <SingleSelectField
                            label="Returned item status"
                            value={form.returnedItemStatus}
                            onChange={setSelect("returnedItemStatus")}
                            choices={RETURNED_STATUS}
                        />
                        <TextField
                            label="Check amount"
                            size="small"
                            type="number"
                            value={form.checkAmount}
                            onChange={setField("checkAmount")}
                            fullWidth
                        />
                        {editingId ? (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                                    From the order
                                </Typography>
                                <LookupField label="Order number" value={form.orderNumber} />
                                <LookupField label="Customer" value={form.customerName} />
                                <LookupField label="Platform" value={form.platform} />
                                <LookupField label="SKU" value={form.skuName} />
                                <LookupField label="Item" value={form.itemName} />
                            </>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </>
    );
}
